import { useEffect, useRef } from 'react';
import type { ApolloCache } from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import { isEmptyString, isNil, isNotNil } from '~/utilities/type-guards';
import { useUploadContext } from './useUploadContext';
import type { FileState } from './types';

interface CallbacksReturnObject {
  useOnFilesAdded: (callback: () => void) => void;
  useOnUploadStarted: (callback: () => void) => void;
  useOnUploadFinished: (callback: () => void) => void;
  useOnMediaLimitExceeded: (callback: (mediaLimitExceededMessage: string) => void) => void;
  useOnUploadSuccess: (callback: (currentEntry: FileState) => void) => void;
  useOnUploadError: (callback: (currentEntry: FileState) => void) => void;
  useApolloUpdate: (
    callback: (cache: ApolloCache<object>, apolloUpdateResponse: object) => void,
  ) => void;
}

type ApolloUpdateResponse = object | null | undefined;

export const GLOBAL_CALLBACKS_IDENTIFIER = 'upload-provider-global-callbacks';

const usePrevious = <T>(value: T): T | undefined => {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export const getCallbacks = (cid: string): CallbacksReturnObject => ({
  useOnFilesAdded: (callback: () => void) => {
    const { uploadQueue } = useUploadContext(cid);
    const previousUploadQueue = usePrevious<FileState[]>(uploadQueue);

    useEffect(() => {
      const curLen = uploadQueue.length;
      const prevLen = previousUploadQueue ? previousUploadQueue.length : 0;
      if (curLen > prevLen) {
        callback();
      }
    }, [uploadQueue, previousUploadQueue, callback]);
  },

  useOnUploadStarted: (callback: () => void) => {
    const { isUploadStarted } = useUploadContext(cid);

    useEffect(() => {
      if (isUploadStarted) {
        callback();
      }
    }, [isUploadStarted, callback]);
  },

  useOnUploadFinished: (callback: () => void) => {
    const context = useUploadContext(cid);

    useEffect(() => {
      if (context.isUploadFinished) {
        callback();
      }
    }, [context.isUploadFinished, callback]);
  },

  useOnMediaLimitExceeded: (callback: (mediaLimitExceededMessage: string) => void) => {
    const { mediaLimitExceededMessage: message } = useUploadContext(cid);
    const previousMessage = usePrevious(message);

    useEffect(() => {
      // message gets reset, only fire the callback once when the message is 'flipped'
      if (isNotNil(message) && (isNil(previousMessage) || isEmptyString(previousMessage))) {
        callback(message);
      }
    }, [callback, message, previousMessage]);
  },

  useOnUploadSuccess: (callback: (currentEntry: FileState) => void) => {
    const { uploadQueue } = useUploadContext(cid);
    const previousUploadQueue = usePrevious<FileState[]>(uploadQueue);

    useEffect(() => {
      for (let i = 0; i < uploadQueue.length; i += 1) {
        const currentEntry = uploadQueue[i];
        const previousEntry = previousUploadQueue?.[i];

        if (!previousEntry?.isDone && currentEntry.isDone && !currentEntry.error) {
          callback(currentEntry);
        }
      }
    }, [uploadQueue, previousUploadQueue, callback]);
  },

  useOnUploadError: (callback: (currentEntry: FileState) => void) => {
    const { uploadQueue } = useUploadContext(cid);
    const previousUploadQueue = usePrevious(uploadQueue);

    useEffect(() => {
      for (let i = 0; i < uploadQueue.length; i += 1) {
        const currentEntry = uploadQueue[i];
        const previousEntry = previousUploadQueue?.[i];

        if (!previousEntry?.error && currentEntry.error) {
          callback(currentEntry);
        }
      }
    }, [uploadQueue, previousUploadQueue, callback]);
  },

  useApolloUpdate: (
    callback: (cache: ApolloCache<object>, apolloUpdateResponse: object) => void,
  ) => {
    const { cache } = useApolloClient();
    const { apolloUpdateResponse } = useUploadContext(cid);
    const previousApolloUpdateResponse = usePrevious<ApolloUpdateResponse>(apolloUpdateResponse);

    useEffect(() => {
      if (apolloUpdateResponse && isNil(previousApolloUpdateResponse)) {
        callback(cache, apolloUpdateResponse);
      }
    }, [apolloUpdateResponse, previousApolloUpdateResponse, callback, cache]);
  },
});
